<script setup>
import { ref } from "vue";
import axios from "axios";

const endpoint = "https://formspree.io/f/xojyvlny";

const form = ref({
  name: "",
  email: "",
  message: "",
});

const status = ref({
  loading: false,
  success: false,
  error: false,
  message: "",
});

const handleSubmit = async () => {
  status.value.loading = true;
  status.value.success = false;
  status.value.error = false;

  try {
    const response = await axios.post(endpoint, form.value);
    if (response.status === 200) {
      status.value.success = true;
      status.value.message = "¡Gracias! Tu mensaje ha sido enviado correctamente.";
      form.value = { name: "", email: "", message: "" }; // Reset form
    } else {
      throw new Error();
    }
  } catch (error) {
    status.value.error = true;
    status.value.message = "Hubo un error al enviar el mensaje. Inténtalo de nuevo más tarde.";
  } finally {
    status.value.loading = false;
  }
};
</script>

<template>
  <section id="contact" class="contact-section">
    <div class="contact-inner">
      <div class="contact-header">
        <h2 class="section-title">¿Tienes alguna duda?</h2>
        <p class="section-subtitle">Estamos aquí para ayudarte. Envíanos tus comentarios o consultas.</p>
      </div>

      <div class="contact-grid">
        <div class="contact-info">
          <div class="info-item">
            <span class="info-icon">📧</span>
            <div>
              <h4>Correo electrónico</h4>
              <p>info@cucuruchonline.com</p>
            </div>
          </div>
          <div class="info-item">
            <span class="info-icon">📍</span>
            <div>
              <h4>Ubicación</h4>
              <p>Haciendo la Semana Santa más digital en Guatemala</p>
            </div>
          </div>
        </div>

        <form @submit.prevent="handleSubmit" class="contact-form">
          <div class="form-group">
            <label for="name">Nombre completo</label>
            <input 
              v-model="form.name" 
              type="text" 
              id="name" 
              placeholder="Ej: Juan Pérez" 
              required
              :disabled="status.loading"
            />
          </div>

          <div class="form-group">
            <label for="email">Tu correo</label>
            <input 
              v-model="form.email" 
              type="email" 
              id="email" 
              placeholder="tu@email.com" 
              required
              :disabled="status.loading"
            />
          </div>

          <div class="form-group">
            <label for="message">Mensaje</label>
            <textarea 
              v-model="form.message" 
              id="message" 
              rows="4" 
              placeholder="¿En qué podemos ayudarte?" 
              required
              :disabled="status.loading"
            ></textarea>
          </div>

          <button type="submit" class="btn-primary full-width" :disabled="status.loading">
            <span v-if="!status.loading">Enviar Mensaje →</span>
            <span v-else class="spinner"></span>
          </button>

          <transition name="fade">
            <div v-if="status.success" class="status-msg success">
              {{ status.message }}
            </div>
          </transition>
          <transition name="fade">
            <div v-if="status.error" class="status-msg error">
              {{ status.message }}
            </div>
          </transition>
        </form>
      </div>
    </div>
  </section>
</template>

<style scoped>
.contact-section {
  padding: 100px 32px;
  background: #fff; /* Fundo claro para contrastar con el CTA oscuro previo */
}

.contact-inner {
  max-width: 1000px;
  margin: 0 auto;
}

.contact-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 12px;
}

.section-subtitle {
  color: #666;
  font-size: 1.05rem;
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 60px;
  align-items: start;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.info-item {
  display: flex;
  gap: 16px;
  align-items: center;
}

.info-icon {
  font-size: 1.8rem;
  background: rgba(123, 47, 247, 0.1);
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

.info-item h4 {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.info-item p {
  color: #666;
  font-size: 0.95rem;
}

.contact-form {
  background: #fff;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1a1a2e;
}

.form-group input,
.form-group textarea {
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  font-family: inherit;
  font-size: 0.95rem;
  transition: all 0.25s;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #7b2ff7;
  box-shadow: 0 0 0 4px rgba(123, 47, 247, 0.1);
}

.full-width {
  width: 100%;
  margin-top: 10px;
}

.btn-primary {
  background: linear-gradient(135deg, #7b2ff7, #4a1b9a);
  color: #fff;
  border: none;
  padding: 14px 28px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.25s;
  box-shadow: 0 6px 20px rgba(123, 47, 247, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(123, 47, 247, 0.45);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: rotate 0.8s linear infinite;
}

@keyframes rotate {
  to { transform: rotate(360deg); }
}

.status-msg {
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 10px;
}

.status-msg.success {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #10b981;
}

.status-msg.error {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #ef4444;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .contact-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .contact-form {
    padding: 24px;
  }
}
</style>
